import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import DateField from "@/components/ui/date-field";
import DriverSelect from "@/components/ui/driver-select";
import EndCustomerSelect from "@/components/ui/end-customer-select";
import { Label } from "@/components/ui/label";
import ReferencePointSelect from "@/components/ui/reference-point-select";
import VehicleSelect from "@/components/ui/vehicle-select";
import { AuthContext } from "@/contexts/auth/AuthContext";
import type {
  EndCustomer,
  ReferencePoint,
  RouteCalculationResponse,
  VehicleRoutingRequest,
} from "@/interfaces";
import { errorHandler } from "@/lib/utils";
import { referencePointService } from "@/services/api/referencePoints";
import routeService, { endCustomerService } from "@/services/api/routeService";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Building2,
  Calendar,
  Clock,
  MapPin,
  RouteIcon,
  Truck,
  User,
} from "lucide-react";
import { useContext, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router";
import { toast } from "sonner";
import { z } from "zod";
import InteractiveRouteMap from "./InteractiveRouteMap";

const formSchema = z.object({
  reference_point_id: z.number({
    message: "Ponto de partida é obrigatório",
  }),
  vehicle_id: z.number({
    message: "Veículo é obrigatório",
  }),
  driver_id: z.number().nullable().optional(),
  customer_ids: z
    .array(z.number())
    .min(1, "Pelo menos um cliente é obrigatório"),
  date: z.string().min(1, "Data é obrigatória"),
});

type FormValues = z.infer<typeof formSchema>;

const RouteForm = () => {
  const [loading, setLoading] = useState(false);
  const [routeData, setRouteData] = useState<RouteCalculationResponse | null>(
    null,
  );
  const [showResults, setShowResults] = useState(false);
  const [customers, setCustomers] = useState<EndCustomer[]>([]);
  const [referencePoints, setReferencePoints] = useState<ReferencePoint[]>([]);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      reference_point_id: 0,
      driver_id: null,
      vehicle_id: 0,
      customer_ids: [],
      date: "",
    },
  });

  const authContext = useContext(AuthContext);
  const navigate = useNavigate();
  const { id } = useParams();
  const userId = authContext?.userId;
  const selectedDate = form.watch("date");
  const selectedCustomers = form.watch("customer_ids");
  const selectedReferencePoint = form.watch("reference_point_id");
  const selectedVehicle = form.watch("vehicle_id");
  const selectedDriver = form.watch("driver_id");

  useEffect(() => {
    const loadData = async () => {
      if (!userId) return;

      try {
        const customersResponse = await endCustomerService.indexAll();
        setCustomers(customersResponse.data || []);

        const referencePointsResponse = await referencePointService.index({
          page: 1,
        });
        setReferencePoints(referencePointsResponse.data || []);
      } catch (err) {
        errorHandler(err, "Erro ao carregar dados");
      }
    };

    loadData();
  }, [userId, id, form]);

  const clearAll = () => {
    form.reset({
      reference_point_id: 0,
      vehicle_id: 0,
      customer_ids: [],
      date: "",
    });

    setRouteData(null);
    setShowResults(false);

    form.reset();
  };

  const calculateRoute = async () => {
    if (
      !selectedVehicle ||
      !selectedDate ||
      selectedCustomers.length === 0 ||
      !selectedReferencePoint
    ) {
      toast.error(
        "Por favor, selecione o veículo, data, sede e pelo menos um cliente.",
      );
      return;
    }

    try {
      setLoading(true);

      const request: VehicleRoutingRequest = {
        customer_ids: selectedCustomers,
        vehicle_id: selectedVehicle,
        ...(selectedDriver &&
          selectedDriver > 0 && { driver_id: selectedDriver }),
        date: selectedDate,
        reference_point_id: selectedReferencePoint,
        vehicle_type: "auto",
        vehicle_axles: 2,
        gas_price: 5.5,
        efficiency: 12,
        type_of_load: "",
        current_currency: "BRL",
        full_route: true,
      };

      const response = await routeService.calculateVehicleRouting(request);
      setRouteData(response.data);
      setShowResults(true);
      toast.success("Rota calculada com sucesso!");
    } catch (err) {
      errorHandler(err, "Erro ao calcular rota");
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (durationString: string) => {
    const parts = durationString.split(":");
    if (parts.length >= 2) {
      const hours = Number.parseInt(parts[0]);
      const minutes = Number.parseInt(parts[1]);
      if (hours > 0) {
        return `${hours}h ${minutes}min`;
      }
      return `${minutes}min`;
    }
    return durationString;
  };

  const getOptimizedCustomerOrder = (): EndCustomer[] => {
    if (!routeData?.osrm_route?.waypoints) {
      return customers.filter((customer) =>
        selectedCustomers.includes(customer.id),
      );
    }

    const waypoints = routeData.osrm_route.waypoints;

    const customerWaypoints = waypoints.slice(1, -1);

    const orderedCustomers: EndCustomer[] = [];

    customerWaypoints.forEach((waypoint) => {
      const [lng, lat] = waypoint.location;

      let closestCustomer: EndCustomer | null = null;
      let minDistance = Number.POSITIVE_INFINITY;

      customers.forEach((customer) => {
        if (!selectedCustomers.includes(customer.id)) return;
        if (orderedCustomers.find((c) => c.id === customer.id)) return;

        const customerLat = Number(customer.latitude);
        const customerLng = Number(customer.longitude);

        if (isNaN(customerLat) || isNaN(customerLng)) return;

        const distance = Math.sqrt(
          Math.pow(lat - customerLat, 2) + Math.pow(lng - customerLng, 2),
        );

        const addressMatch =
          customer.address &&
          waypoint.name &&
          (customer.address
            .toLowerCase()
            .includes(waypoint.name.toLowerCase()) ||
            waypoint.name
              .toLowerCase()
              .includes(customer.address.toLowerCase()));

        if (addressMatch || distance < minDistance) {
          minDistance = distance;
          closestCustomer = customer;
        }
      });

      if (closestCustomer) {
        orderedCustomers.push(closestCustomer);
      }
    });

    selectedCustomers.forEach((customerId) => {
      if (!orderedCustomers.find((c) => c.id === customerId)) {
        const customer = customers.find((c) => c.id === customerId);
        if (customer) {
          orderedCustomers.push(customer);
        }
      }
    });

    return orderedCustomers;
  };

  const handleMapCustomerSelect = (customerId: number) => {
    if (showResults) return;

    const currentCustomers = form.getValues("customer_ids");
    if (currentCustomers.includes(customerId)) {
      form.setValue(
        "customer_ids",
        currentCustomers.filter((id) => id !== customerId),
      );
    } else {
      form.setValue("customer_ids", [...currentCustomers, customerId]);
    }
  };

  const handleMapReferencePointSelect = (referencePointId: number) => {
    if (showResults) return;

    form.setValue("reference_point_id", referencePointId);
  };

  return (
    <FormProvider {...form}>
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                Nova Rota
              </h1>
              <p className="text-gray-600 mt-1 dark:text-gray-400">
                Crie uma nova rota otimizada para suas entregas
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="dark:text-gray-200 bg-transparent"
                onClick={() => navigate("/vehicle-routing")}
              >
                Cancelar
              </Button>
              {!showResults && (
                <Button
                  variant="outline"
                  onClick={clearAll}
                  className="dark:bg-gray-700 dark:hover:bg-gray-600 dark:active:bg-gray-500 dark:text-white dark:border-none dark:shadow-sm bg-transparent"
                >
                  Limpar Tudo
                </Button>
              )}
              {showResults && (
                <Button
                  variant="outline"
                  onClick={() => {
                    clearAll();
                  }}
                  className="dark:bg-gray-700 dark:hover:bg-gray-600 dark:active:bg-gray-500 dark:text-white dark:border-none dark:shadow-sm bg-transparent"
                >
                  Nova Rota
                </Button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-1 space-y-6">
              <Card className="dark:bg-gray-800 dark:border-gray-700">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 dark:text-gray-100">
                    <Building2 className="w-5 h-5" />
                    Selecionar ponto de partida
                  </CardTitle>
                  <CardDescription className="dark:text-gray-400">
                    {showResults
                      ? "Ponto de partida selecionado"
                      : "Escolha um ponto de partida ou clique no mapa"}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Label
                    htmlFor="reference-point"
                    className="dark:text-gray-200"
                  >
                    Ponto de partida
                  </Label>
                  <div className="mt-2">
                    <ReferencePointSelect
                      value={selectedReferencePoint}
                      onChange={(value) =>
                        form.setValue("reference_point_id", value as number)
                      }
                      placeholder="Selecione um ponto de partida"
                      useFormControl={false}
                      disabled={showResults}
                    />
                  </div>
                </CardContent>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 dark:text-gray-100">
                    <MapPin className="w-5 h-5" />
                    Selecionar Clientes
                  </CardTitle>
                  <CardDescription className="dark:text-gray-400">
                    {showResults
                      ? "Clientes selecionados para a rota"
                      : "Escolha os clientes para incluir na rota ou clique nos marcadores no mapa"}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Label htmlFor="customers" className="dark:text-gray-200">
                    Clientes
                  </Label>
                  <div className="mt-2">
                    <EndCustomerSelect
                      value={selectedCustomers}
                      onChange={(value) =>
                        form.setValue("customer_ids", value as number[])
                      }
                      multiple
                      placeholder="Selecione os clientes"
                      useFormControl={false}
                      disabled={showResults}
                    />
                  </div>
                  {selectedCustomers.length > 0 && (
                    <div className="text-sm text-gray-500 mt-2 dark:text-gray-400">
                      {selectedCustomers.length} cliente(s) selecionado(s)
                    </div>
                  )}
                </CardContent>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 dark:text-gray-100">
                    <Truck className="w-5 h-5" />
                    Selecionar Veículo
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Label htmlFor="vehicle" className="dark:text-gray-200">
                    Veículo
                  </Label>
                  <div className="mt-2 flex flex-col">
                    <VehicleSelect
                      value={selectedVehicle}
                      onChange={(value) =>
                        form.setValue("vehicle_id", value as number)
                      }
                    />
                  </div>
                </CardContent>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 dark:text-gray-100">
                    <User className="w-5 h-5" />
                    Selecionar Motorista
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Label htmlFor="driver" className="dark:text-gray-200">
                    Motorista
                  </Label>
                  <div className="mt-2">
                    <DriverSelect
                      value={selectedDriver || undefined}
                      onChange={(value) =>
                        form.setValue("driver_id", value as number)
                      }
                    />
                  </div>
                </CardContent>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 dark:text-gray-100">
                    <Calendar className="w-5 h-5" />
                    Data da Entrega
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <DateField
                    form={form}
                    name="date"
                    label="Data"
                    placeholder="Selecione a data"
                    className="dark:text-gray-200"
                  />
                </CardContent>
              </Card>

              {/* Botões de Ação */}
              {!showResults && (
                <div className="space-y-2 dark:text-gray-200">
                  <Button
                    className="w-full bg-primary-500 dark:bg-primary-400 text-white dark:hover:opacity-90 "
                    onClick={calculateRoute}
                    disabled={
                      loading ||
                      !selectedVehicle ||
                      !selectedDate ||
                      !selectedReferencePoint ||
                      selectedCustomers.length === 0
                    }
                  >
                    <RouteIcon className="w-4 h-4 mr-2 dark:text-white" />
                    {loading ? "Calculando..." : "Calcular Rota"}
                  </Button>
                </div>
              )}
            </div>

            {/* Área Principal */}
            <div className="lg:col-span-2 space-y-6">
              {/* Mapa */}
              <Card className="dark:bg-gray-800 dark:border-gray-700">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 dark:text-gray-100">
                    <RouteIcon className="w-5 h-5" />
                    {showResults ? "Rota Calculada" : "Planejamento da Rota"}
                  </CardTitle>
                  <CardDescription className="dark:text-gray-400">
                    {showResults
                      ? "Visualização da rota otimizada"
                      : "Clique nos marcadores para selecionar pontos de partida e clientes"}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <InteractiveRouteMap
                    customers={customers}
                    referencePoints={referencePoints}
                    selectedCustomers={selectedCustomers}
                    selectedReferencePoint={selectedReferencePoint}
                    onCustomerSelect={handleMapCustomerSelect}
                    onReferencePointSelect={handleMapReferencePointSelect}
                    routeData={showResults ? routeData : null}
                  />
                </CardContent>
              </Card>

              {/* Estatísticas da Rota */}
              {showResults && routeData && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card className="dark:bg-gray-800 dark:border-gray-700">
                    <CardContent className="flex items-center gap-3 p-6">
                      <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
                        <RouteIcon className="w-5 h-5 text-blue-600 dark:text-blue-300" />
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                          {routeData.distance.toFixed(1).replace(".", ",")} km
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          Distância Total
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="dark:bg-gray-800 dark:border-gray-700">
                    <CardContent className="flex items-center gap-3 p-6">
                      <div className="w-10 h-10 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center">
                        <Clock className="w-5 h-5 text-green-600 dark:text-green-300" />
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                          {routeData.duration
                            ? formatDuration(String(routeData.duration))
                            : "N/A"}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          Tempo Estimado
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="dark:bg-gray-800 dark:border-gray-700">
                    <CardContent className="flex items-center gap-3 p-6">
                      <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900 rounded-lg flex items-center justify-center">
                        <MapPin className="w-5 h-5 text-orange-600 dark:text-orange-300" />
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                          {selectedCustomers.length}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          Paradas
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Ordem de Entrega */}
              {showResults && routeData && (
                <Card className="dark:bg-gray-800 dark:border-gray-700">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 dark:text-gray-100">
                      <Clock className="w-5 h-5" />
                      Ordem de Entrega
                    </CardTitle>
                    <CardDescription className="dark:text-gray-400">
                      Sequência otimizada das entregas
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {/* Mostrar ponto de partida se selecionado */}
                      {selectedReferencePoint && (
                        <div className="flex items-start gap-3 p-3 border rounded-lg dark:border-gray-700 bg-blue-50 dark:bg-blue-900/20">
                          <div className="w-8 h-8 bg-blue-100 dark:bg-blue-700 text-blue-700 dark:text-blue-200 rounded-full flex items-center justify-center text-sm font-medium">
                            🏁
                          </div>
                          <div className="flex-1">
                            <div className="font-medium text-gray-900 dark:text-gray-100">
                              {referencePoints.find(
                                (rp) => rp.id === selectedReferencePoint,
                              )?.name || "Ponto de Partida"}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1 mt-1">
                              <Building2 className="w-3 h-3" />
                              Ponto de Partida
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Mostrar clientes na ordem otimizada dos waypoints */}
                      {getOptimizedCustomerOrder().map((customer, index) => (
                        <div
                          key={customer.id}
                          className="flex items-start gap-3 p-3 border rounded-lg dark:border-gray-700"
                        >
                          <div className="w-8 h-8 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-full flex items-center justify-center text-sm font-medium">
                            {index + 1}
                          </div>
                          <div className="flex-1">
                            <div className="font-medium text-gray-900 dark:text-gray-100">
                              {customer.name}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1 mt-1">
                              <MapPin className="w-3 h-3" />
                              {customer.address}
                            </div>
                            {customer.phone_number && (
                              <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                Contato: {customer.area_code}{" "}
                                {customer.phone_number}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </div>
    </FormProvider>
  );
};

export default RouteForm;
